import { Button, Form, Input, Modal, Skeleton } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import React, { useEffect, useMemo, useState } from "react";
import { CSVLink } from "react-csv";
import { toast } from "react-toastify";
import { AiOutlineDownload, AiOutlineEdit, AiOutlineSearch } from "react-icons/ai";
import { BsBuildings, BsPeople, BsPlusLg } from "react-icons/bs";
import { Link } from "react-router-dom";
import UserPrivateComponent from "../PrivateRoutes/UserPrivateComponent";
import getPermissions from "../../utils/getPermissions";
import { addDepartment, getDepartments, updateDepartment } from "./departmentApis";

const departmentAccents = [
  "from-[#023f88] to-[#0b5cae]",
  "from-sky-500 to-blue-600",
  "from-indigo-500 to-violet-600",
  "from-amber-500 to-orange-500",
  "from-rose-500 to-pink-600",
];

// Add a department, or rename one when `department` is given
export function DepartmentForm({ department, onSaved }) {
  const [loader, setLoader] = useState(false);
  const [form] = Form.useForm();

  const onFinish = async (values) => {
    setLoader(true);
    const resp = department
      ? await updateDepartment(department.id, values)
      : await addDepartment(values);
    setLoader(false);

    if (resp.message === "success") {
      form.resetFields();
      onSaved(resp.data);
    }
  };

  const onFinishFailed = () => {
    toast.warning("Failed at saving department");
    setLoader(false);
  };

  return (
    <Form
      form={form}
      layout='vertical'
      requiredMark={false}
      initialValues={{ name: department?.name }}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      autoComplete='off'
    >
      <Form.Item
        label={<span className='font-medium text-slate-700'>Department name</span>}
        name='name'
        rules={[
          {
            required: true,
            message: "Please input department name!",
          },
        ]}
      >
        <Input size='large' placeholder='e.g. Computer Science' />
      </Form.Item>

      <Button
        type='primary'
        size='large'
        htmlType='submit'
        block
        loading={loader}
        className='cp-navy-btn rounded-xl'
      >
        {department ? "Save changes" : "Add New Department"}
      </Button>
    </Form>
  );
}

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <div className='flex items-center gap-4 rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200/70'>
    <div
      className={`flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br ${accent} text-white shadow-md`}
    >
      <Icon className='text-2xl' />
    </div>
    <div>
      <p className='text-3xl font-extrabold leading-none text-slate-900'>{value}</p>
      <p className='mt-1 text-sm font-medium text-slate-600'>{label}</p>
    </div>
  </div>
);

const DepartmentCard = ({ department, index, userCount, canEdit, onEdit }) => (
  <div className='group flex flex-col rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200/70 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl'>
    <div className='flex items-start justify-between'>
      <div
        className={`flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br ${
          departmentAccents[index % departmentAccents.length]
        } text-white shadow-md transition-transform duration-300 group-hover:scale-110`}
      >
        <BsBuildings className='text-2xl' />
      </div>
      {userCount !== undefined && (
        <span className='inline-flex items-center gap-1.5 rounded-full bg-blue-100 px-3 py-1 text-xs font-bold text-[#023f88] ring-1 ring-blue-200'>
          <BsPeople />
          {userCount} {userCount === 1 ? "user" : "users"}
        </span>
      )}
    </div>

    <h3 className='mt-5 text-lg font-bold text-slate-900'>{department.name}</h3>
    <p className='text-sm text-slate-600'>
      Created {dayjs(department.createdAt).format("DD MMM YYYY")}
    </p>

    <div className='mt-5 flex gap-2'>
      <UserPrivateComponent permission={"readSingle-department"}>
        <Link
          to={`/admin/setup/department/${department.id}/`}
          className='flex-1 rounded-xl bg-gradient-to-r from-[#023f88] to-[#0b5cae] px-3 py-2 text-center text-sm font-semibold text-white shadow transition hover:opacity-90 hover:text-white'
        >
          View details
        </Link>
      </UserPrivateComponent>
      {canEdit && (
        <button
          type='button'
          onClick={() => onEdit(department)}
          className='inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-[#023f88] px-3 py-2 text-sm font-semibold text-[#023f88] transition hover:bg-[#023f88] hover:text-white'
        >
          <AiOutlineEdit />
          Rename
        </button>
      )}
    </div>
  </div>
);

const DepartmentTable = () => {
  const [list, setList] = useState(null);
  const [userCounts, setUserCounts] = useState(null);
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null); // { department } — department is undefined when adding
  const permissions = getPermissions() || [];
  const canEdit = permissions.includes("update-department");

  useEffect(() => {
    getDepartments()
      .then((d) => setList(d))
      .catch((error) => console.log(error));

    // users only store a departmentId, so count them here; skipped without readAll-customer
    if (permissions.includes("readAll-customer")) {
      axios
        .get(`customer?query=all`)
        .then(({ data }) =>
          setUserCounts(
            (data || []).reduce((acc, u) => {
              if (u.departmentId) acc[u.departmentId] = (acc[u.departmentId] || 0) + 1;
              return acc;
            }, {})
          )
        )
        .catch((error) => console.log(error));
    }
    // eslint-disable-next-line
  }, []);

  const departments = useMemo(() => list || [], [list]);
  const filteredDepartments = useMemo(() => {
    const term = search.trim().toLowerCase();
    return term
      ? departments.filter((d) => d.name?.toLowerCase().includes(term))
      : departments;
  }, [departments, search]);

  const assignedUsers = userCounts
    ? Object.values(userCounts).reduce((sum, n) => sum + n, 0)
    : 0;
  const emptyDepartments = userCounts
    ? departments.filter((d) => !userCounts[d.id]).length
    : 0;

  const onSaved = (saved) => {
    if (saved) {
      setList((prev) => {
        const current = prev || [];
        return current.some((d) => d.id === saved.id)
          ? current.map((d) => (d.id === saved.id ? { ...d, ...saved } : d))
          : [...current, saved];
      });
    }
    setModal(null);
  };

  return (
    <UserPrivateComponent permission={"readAll-department"}>
      <div className='max-w-7xl mx-auto py-6 md:py-8'>
        {/* Header */}
        <div className='relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#011f44] via-[#023f88] to-[#0b5cae] px-6 py-8 md:px-10 text-white shadow-xl'>
          <div className='absolute -top-16 -right-16 h-56 w-56 rounded-full bg-white/10 blur-2xl' />
          <div className='absolute -bottom-20 left-1/3 h-48 w-48 rounded-full bg-sky-300/20 blur-2xl' />

          <div className='relative flex flex-col gap-6 md:flex-row md:items-center md:justify-between'>
            <div>
              <span className='inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-medium uppercase tracking-wide backdrop-blur-sm'>
                <BsBuildings />
                Organisation
              </span>
              <h1 className='mt-4 text-3xl md:text-4xl font-extrabold tracking-tight text-white'>
                Departments
              </h1>
              <p className='mt-2 max-w-xl text-blue-50/90'>
                Organise your users into departments and keep their structure up to
                date.
              </p>
            </div>

            <div className='flex flex-wrap gap-3'>
              <UserPrivateComponent permission={"create-department"}>
                <button
                  type='button'
                  onClick={() => setModal({})}
                  className='inline-flex items-center gap-2 rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-[#023f88] shadow-lg transition hover:-translate-y-0.5 hover:shadow-xl'
                >
                  <BsPlusLg />
                  Add Department
                </button>
              </UserPrivateComponent>
              {departments.length > 0 && (
                <CSVLink
                  data={departments}
                  filename='departments'
                  className='inline-flex items-center gap-2 rounded-xl bg-white/15 px-4 py-2.5 text-sm font-semibold text-white backdrop-blur-sm transition hover:bg-white/25 hover:text-white'
                >
                  <AiOutlineDownload className='text-lg' />
                  Download CSV
                </CSVLink>
              )}
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className={`mt-6 grid gap-4 ${userCounts ? "sm:grid-cols-3" : "sm:grid-cols-1"}`}>
          <StatCard
            icon={BsBuildings}
            label='Total departments'
            value={departments.length}
            accent='from-[#023f88] to-[#0b5cae]'
          />
          {userCounts && (
            <>
              <StatCard
                icon={BsPeople}
                label='Users assigned'
                value={assignedUsers}
                accent='from-green-500 to-green-600'
              />
              <StatCard
                icon={BsBuildings}
                label='Departments without users'
                value={emptyDepartments}
                accent='from-amber-500 to-orange-500'
              />
            </>
          )}
        </div>

        {/* Search */}
        <div className='mt-8 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between'>
          <div>
            <h2 className='text-xl md:text-2xl font-bold text-slate-900'>All departments</h2>
            <p className='text-sm text-slate-600'>
              {filteredDepartments.length} of {departments.length} departments
            </p>
          </div>
          <Input
            allowClear
            size='large'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            prefix={<AiOutlineSearch className='text-slate-400' />}
            placeholder='Search departments…'
            className='rounded-xl sm:max-w-xs'
          />
        </div>

        {/* Departments */}
        {!list ? (
          <div className='mt-5 grid gap-5 sm:grid-cols-2 xl:grid-cols-3'>
            {[0, 1, 2].map((i) => (
              <div key={i} className='rounded-2xl bg-white p-6 ring-1 ring-slate-200/70'>
                <Skeleton active />
              </div>
            ))}
          </div>
        ) : filteredDepartments.length ? (
          <div className='mt-5 grid gap-5 sm:grid-cols-2 xl:grid-cols-3'>
            {filteredDepartments.map((department, i) => (
              <DepartmentCard
                key={department.id}
                department={department}
                index={i}
                userCount={userCounts ? userCounts[department.id] || 0 : undefined}
                canEdit={canEdit}
                onEdit={(d) => setModal({ department: d })}
              />
            ))}
          </div>
        ) : (
          <div className='mt-5 rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center'>
            <BsBuildings className='mx-auto text-4xl text-slate-300' />
            <p className='mt-3 font-semibold text-slate-700'>No departments found</p>
            <p className='mt-1 text-sm text-slate-600'>
              Try a different search or add a new department.
            </p>
          </div>
        )}
      </div>

      <Modal
        open={!!modal}
        onCancel={() => setModal(null)}
        footer={null}
        centered
        destroyOnClose
        title={
          <div className='flex items-center gap-3'>
            <div className='flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-[#023f88] to-[#0b5cae] text-white'>
              {modal?.department ? <AiOutlineEdit /> : <BsPlusLg />}
            </div>
            <div>
              <p className='text-lg font-bold text-slate-900'>
                {modal?.department ? "Rename department" : "Add new department"}
              </p>
              <p className='text-sm font-normal text-slate-600'>
                {modal?.department
                  ? modal.department.name
                  : "Users can be assigned to it right away."}
              </p>
            </div>
          </div>
        }
      >
        <div className='pt-2'>
          <DepartmentForm department={modal?.department} onSaved={onSaved} />
        </div>
      </Modal>
    </UserPrivateComponent>
  );
};

export default DepartmentTable;
